import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/Dialog';
import { Loader2, Plus, Search, Copy, Check } from 'lucide-react';
import { useToast } from '../components/ui/Toast';
import { fetchClientIntegrationsBulk } from '../services/clientService';
import { fetchCarriers } from '../services/carrierService';
import { createIntegration, fetchIntegrationLogs } from '../services/integrationService';
import { deployConfiguration, cancelScheduledDeployment } from '../services/configurationService';
import { archiveRevision } from '../services/revisionService';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import logger from '../utils/logger';

/**
 * Reusable Create New Version button component
 * Handles conditional rendering and loading state
 */
const CreateNewVersionButton = ({
  integration,
  revision,
  onCreateNewVersion,
  className = '',
}) => {
  // Don't render if can't create new version
  // Note: Backend already encodes all business logic (status, draft/scheduled existence)
  if (!revision.allowed_actions?.can_create_new_version) {
    return null;
  }

  return (
    <Button
      size="sm"
      onClick={() => onCreateNewVersion(integration.id)}
      className={className}
    >
      Create New Version
    </Button>
  );
};

export default function IntegrationDetailsPage() {
  usePageTitle('Integration Details');
  const { clientId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [client, setClient] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deployingRevisionId, setDeployingRevisionId] = useState(null);
  const [viewingConfig, setViewingConfig] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    content_type: '',
    display_name: '',
    carrier: '',
  });
  const [formError, setFormError] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [isLogsDialogOpen, setIsLogsDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logsSearchQuery, setLogsSearchQuery] = useState('');
  const [copiedLogId, setCopiedLogId] = useState(null);

  useEffect(() => {
    loadClientIntegrations();
    loadCarriers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadCarriers = async () => {
    try {
      const response = await fetchCarriers({ is_active: true });
      setCarriers(response.results || response || []);
    } catch (err) {
      logger.error('Error loading carriers:', err);
    }
  };

  const loadClientIntegrations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the new bulk API endpoint to fetch all data in a single call
      const bulkData = await fetchClientIntegrationsBulk(clientId);

      // Set client info
      setClient({
        id: bulkData.id,
        name: bulkData.name,
      });

      // Filter each integration's revisions to show only the current LIVE, DRAFT, and SCHEDULED
      const integrationsWithFilteredRevisions = (bulkData.integrations || []).map((integration) => {
        const allRevisions = integration.revisions || [];
        const liveRevision = allRevisions.find(r => r.status === 'LIVE');
        const draftRevision = allRevisions.find(r => r.status === 'DRAFT');
        const scheduledRevision = allRevisions.find(r => r.status === 'SCHEDULED');

        // Build array with LIVE, DRAFT, and SCHEDULED, sorted by revision number (lower on left)
        const sortedRevisions = [liveRevision, draftRevision, scheduledRevision]
          .filter(Boolean)
          .sort((a, b) => a.revision_number - b.revision_number);

        // Add integration ID to each revision for navigation purposes
        const revisionsWithIntegrationId = sortedRevisions.map(revision => ({
          ...revision,
          integration: integration.id,
        }));

        return {
          ...integration,
          revisions: revisionsWithIntegrationId,
        };
      });

      setIntegrations(integrationsWithFilteredRevisions);

    } catch (err) {
      logger.error('Error loading client integrations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddIntegration = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.content_type || !formData.display_name.trim()) {
      setFormError('Please fill in all required fields');
      return;
    }

    try {
      setIsSubmitting(true);

      const integrationData = {
        client: clientId,
        content_type: formData.content_type,
        display_name: formData.display_name.trim(),
        carrier: formData.carrier || null,
      };

      await createIntegration(integrationData);

      setIsAddDialogOpen(false);
      setFormData({
        content_type: '',
        display_name: '',
        carrier: '',
      });

      await loadClientIntegrations();
    } catch (err) {
      logger.error('Error creating integration:', err);
      setFormError(err.message || 'Failed to create integration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setIsAddDialogOpen(false);
    setFormData({
      content_type: '',
      display_name: '',
      carrier: '',
    });
    setFormError(null);
  };

  const handleViewLogs = async (integration) => {
    setSelectedIntegration(integration);
    setIsLogsDialogOpen(true);
    setLogsLoading(true);
    setLogsError(null);
    setLogs([]);

    try {
      const logsData = await fetchIntegrationLogs(integration.id);
      setLogs(logsData);
    } catch (err) {
      logger.error('Error loading logs:', err);
      setLogsError(err.message);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleLogsDialogClose = () => {
    setIsLogsDialogOpen(false);
    setSelectedIntegration(null);
    setLogs([]);
    setLogsError(null);
    setLogsSearchQuery('');
    setCopiedLogId(null);
  };

  const handleCopyLog = async (log) => {
    const logText = `File: ${log.file_name}
Status: ${log.status}
Version: ${log.revision_number}
Trigger: ${log.trigger_type}
Started: ${new Date(log.started_at).toLocaleString()}
Completed: ${log.completed_at ? new Date(log.completed_at).toLocaleString() : 'N/A'}
Duration: ${log.duration_seconds !== null ? `${log.duration_seconds}s` : 'N/A'}
File Size: ${log.file_size_bytes ? `${(log.file_size_bytes / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
Records Processed: ${log.records_processed.toLocaleString()}
Records Failed: ${log.records_failed.toLocaleString()}
Success Rate: ${log.records_processed > 0 ? ((log.records_processed - log.records_failed) / log.records_processed * 100).toFixed(1) + '%' : 'N/A'}`;

    try {
      await navigator.clipboard.writeText(logText);
      setCopiedLogId(log.id);
      setTimeout(() => setCopiedLogId(null), 2000);
    } catch (err) {
      logger.error('Failed to copy:', err);
    }
  };

  const filteredLogs = logs.filter((log) => {
    if (!logsSearchQuery.trim()) return true;
    const query = logsSearchQuery.toLowerCase();
    return (
      log.file_name.toLowerCase().includes(query) ||
      log.status.toLowerCase().includes(query) ||
      log.trigger_type.toLowerCase().includes(query) ||
      log.revision_number.toString().includes(query)
    );
  });

  const contentTypeOptions = [
    { value: 'ELIGIBILITY', label: 'Eligibility' },
    { value: 'MEDICAL_CLAIMS', label: 'Medical Claims' },
    { value: 'DENTAL_CLAIMS', label: 'Dental Claims' },
    { value: 'PHARMACY_CLAIMS', label: 'Pharmacy Claims' },
    { value: 'VISION_CLAIMS', label: 'Vision Claims' },
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'LIVE': { variant: 'success', label: 'LIVE' },
      'DRAFT': { variant: 'secondary', label: 'DRAFT' },
      'SCHEDULED': { variant: 'warning', label: 'SCHEDULED' },
      'ARCHIVED': { variant: 'outline', label: 'ARCHIVED' },
    };

    const config = statusConfig[status] || { variant: 'secondary', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTestStatusBadge = (testStatus) => {
    const statusConfig = {
      'PASSED': { variant: 'success', label: 'Passed' },
      'FAILED': { variant: 'destructive', label: 'Failed' },
      'TESTING': { variant: 'warning', label: 'Testing' },
      'NOT_TESTED': { variant: 'secondary', label: 'Not Tested' },
    };

    const config = statusConfig[testStatus] || { variant: 'secondary', label: testStatus };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const [isNewVersionDialogOpen, setIsNewVersionDialogOpen] = useState(false);
  const [newVersionIntegrationId, setNewVersionIntegrationId] = useState(null);

  const handleCreateNewVersion = async (integrationId) => {
    // Show dialog to choose between starting fresh or using LIVE as base
    setNewVersionIntegrationId(integrationId);
    setIsNewVersionDialogOpen(true);
  };

  const handleNewVersionChoice = (useLiveAsBase) => {
    // Navigate to config page in "new draft" mode without creating DB record
    // The revision will be created when the user explicitly clicks Save
    navigate(`/integrations/${newVersionIntegrationId}/revisions/new/config`, {
      state: { isNewDraft: true, useLiveAsBase },
    });
    setIsNewVersionDialogOpen(false);
    setNewVersionIntegrationId(null);
  };

  const handleDeployRevision = async (integrationId, revisionId, configJson) => {
    try {
      setDeployingRevisionId(revisionId);

      if (!configJson) {
        toast.error('No configuration data found for this revision');
        return;
      }

      await deployConfiguration(integrationId, configJson);
      toast.success('Configuration deployed successfully!');
      await loadClientIntegrations();
    } catch (error) {
      logger.error('Error deploying configuration:', error);
      toast.error(`Deployment failed: ${error.message}`);
    } finally {
      setDeployingRevisionId(null);
    }
  };

  const handleCancelSchedule = async (integrationId) => {
    try {
      setDeployingRevisionId('cancelling');
      await cancelScheduledDeployment(integrationId);
      toast.success('Scheduled deployment cancelled successfully!');
      await loadClientIntegrations();
    } catch (error) {
      logger.error('Error cancelling scheduled deployment:', error);
      toast.error(`Failed to cancel: ${error.message}`);
    } finally {
      setDeployingRevisionId(null);
    }
  };

  const handleDeleteDraft = async (revisionId) => {
    try {
      setDeployingRevisionId('deleting');
      await archiveRevision(revisionId);
      toast.success('Draft deleted successfully!');
      await loadClientIntegrations();
    } catch (error) {
      logger.error('Error deleting draft:', error);
      toast.error(`Failed to delete draft: ${error.message}`);
    } finally {
      setDeployingRevisionId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-destructive">Error: {error}</p>
      </div>
    );
  }

  if (!client && !loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Client not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 lg:p-6 space-y-4 lg:space-y-6">
      {/* Page Header Card */}
      <Card>
        <CardContent className="p-4 sm:p-5 lg:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">{client?.name}</h1>
              <p className="text-sm text-muted-foreground">
                Data Integration Management
              </p>
            </div>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Integration
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Integration Cards */}
      {integrations.map((integration) => (
        <Card key={integration.id}>
          <CardContent className="p-0">
            <div className="space-y-4">
              {/* Integration Header with shaded background */}
              <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-b text-left">
                <h2 className="text-base font-semibold text-left">{integration.display_name}</h2>
                <p className="text-sm text-blue-600 text-left">
                  {integration.description || integration.content_type.replace('_', ' ')}
                </p>
              </div>

              {/* Revisions */}
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4">
                {integration.revisions && integration.revisions.length > 0 ? (
                  <div className={`grid grid-cols-1 ${integration.revisions.length > 1 ? 'lg:grid-cols-2' : ''} gap-4 grid-flow-row`}>
                    {integration.revisions.map((revision, index) => (
                      <div key={revision.id} className={`${integration.revisions.length > 1 && index % 2 === 0 ? 'lg:border-r lg:pr-4' : ''}`}>
                        <div className={`${integration.revisions.length === 1 ? 'grid sm:grid-cols-2 sm:divide-x' : 'relative'}`}>
                          {/* Left content area */}
                          <div className={integration.revisions.length === 1 ? '' : ''}>
                            {/* Version Header */}
                            <div className={`flex flex-wrap items-center gap-2 pb-3 mb-3 border-b ${integration.revisions.length === 1 ? '' : 'pr-0 lg:pr-32'}`}>
                              <span className="font-semibold">Version {revision.revision_number}</span>
                              {getStatusBadge(revision.status)}
                            </div>

                            {/* Version Details */}
                            <div className="space-y-2 text-xs sm:text-sm">
                              {revision.status === 'LIVE' && (
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-muted-foreground whitespace-nowrap">Deployed</span>
                                  <span className="break-words">{formatDate(revision.deployed_at) || '-'}</span>
                                </div>
                              )}
                              {revision.status === 'DRAFT' && (
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-muted-foreground whitespace-nowrap">Last Edited</span>
                                  <span className="break-words">{formatDateTime(revision.last_edited_at || revision.created_at) || '-'}</span>
                                </div>
                              )}
                              {revision.status === 'SCHEDULED' && (
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-muted-foreground whitespace-nowrap">Scheduled</span>
                                  <span className="break-words">{formatDateTime(revision.scheduled_deploy_at) || '-'}</span>
                                </div>
                              )}
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-muted-foreground whitespace-nowrap">Test Status</span>
                                {getTestStatusBadge(revision.test_status)}
                              </div>
                              <div>
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-muted-foreground whitespace-nowrap">Last File</span>
                                  <span className="break-words">{revision.last_file_date ? formatDate(revision.last_file_date) : '-'}</span>
                                </div>
                                {/* Show errors/no errors only for LIVE with last_file_date */}
                                {revision.status === 'LIVE' && revision.last_file_date && revision.last_file_errors && (
                                  <p className="text-red-600 text-xs mt-1">
                                    {revision.last_file_errors}
                                  </p>
                                )}
                                {revision.status === 'LIVE' && revision.last_file_date && !revision.last_file_errors && (
                                  <p className="text-green-600 text-xs mt-1">No errors</p>
                                )}
                                {/* Invisible placeholder for alignment when no error status line is shown */}
                                {(revision.status === 'DRAFT' || revision.status === 'SCHEDULED' || (revision.status === 'LIVE' && !revision.last_file_date)) && (
                                  <p className="text-xs mt-1 invisible">Placeholder</p>
                                )}
                              </div>
                            </div>

                            {/* Action Buttons for single revision - LIVE status */}
                            {revision.status === 'LIVE' && integration.revisions.length === 1 && (
                              <div className="mt-4 flex flex-wrap gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate(`/integrations/${integration.id}/revisions/${revision.id}/config`)}
                                  className="flex-1 sm:flex-none text-xs sm:text-sm"
                                >
                                  View Config
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewLogs(integration)}
                                  className="flex-1 sm:flex-none text-xs sm:text-sm"
                                >
                                  Logs
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Right pane with centered button (single revision - any status) */}
                          {integration.revisions.length === 1 && (
                            <div className="hidden sm:flex items-center justify-center p-4">
                              <CreateNewVersionButton
                                integration={integration}
                                revision={revision}
                                onCreateNewVersion={handleCreateNewVersion}
                                className="text-xs sm:text-sm px-6"
                              />
                            </div>
                          )}

                          {/* Button for mobile (single revision - LIVE status) */}
                          {integration.revisions.length === 1 && (
                            <div className="sm:hidden p-3 pt-0">
                              <CreateNewVersionButton
                                integration={integration}
                                revision={revision}
                                onCreateNewVersion={handleCreateNewVersion}
                                className="w-full text-xs"
                              />
                            </div>
                          )}

                          {/* Button for multiple revisions (positioned absolutely) */}
                          {integration.revisions.length > 1 && (
                            <div className="mt-3 lg:mt-0 lg:absolute lg:top-12 lg:right-4">
                              <CreateNewVersionButton
                                integration={integration}
                                revision={revision}
                                onCreateNewVersion={handleCreateNewVersion}
                                className="w-full lg:w-auto text-xs sm:text-sm"
                              />
                            </div>
                          )}
                        </div>

                        {/* Action Buttons for multiple revisions */}
                        {revision.status === 'LIVE' && integration.revisions.length > 1 && (
                          <div className="mt-4 flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/integrations/${integration.id}/revisions/${revision.id}/config`)}
                              className="flex-1 sm:flex-none text-xs sm:text-sm"
                            >
                              View Config
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewLogs(integration)}
                              className="flex-1 sm:flex-none text-xs sm:text-sm"
                            >
                              Logs
                            </Button>
                          </div>
                        )}
                        {revision.status === 'DRAFT' && (
                          <div className="mt-4 flex flex-wrap gap-2">
                            <Tooltip content={revision.allowed_actions?.reasons?.edit}>
                              <Button
                                size="sm"
                                onClick={() => navigate(`/integrations/${integration.id}/revisions/${revision.id}/config`)}
                                disabled={!revision.allowed_actions?.can_edit}
                                className="flex-1 sm:flex-none text-xs sm:text-sm whitespace-nowrap"
                              >
                                Continue Editing
                              </Button>
                            </Tooltip>
                            <Tooltip content={revision.allowed_actions?.reasons?.deploy}>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleDeployRevision(integration.id, revision.id, revision.config_json)}
                                disabled={!revision.allowed_actions?.can_deploy || deployingRevisionId === revision.id}
                                className="flex-1 sm:flex-none text-xs sm:text-sm"
                              >
                                {deployingRevisionId === revision.id ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Deploying...
                                  </>
                                ) : (
                                  'Deploy'
                                )}
                              </Button>
                            </Tooltip>
                            <Tooltip content={revision.allowed_actions?.reasons?.archive}>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteDraft(revision.id)}
                                disabled={!revision.allowed_actions?.can_archive || deployingRevisionId === 'deleting'}
                                className="flex-1 sm:flex-none text-xs sm:text-sm whitespace-nowrap"
                              >
                                {deployingRevisionId === 'deleting' ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Deleting...
                                  </>
                                ) : (
                                  'Delete Draft'
                                )}
                              </Button>
                            </Tooltip>
                          </div>
                        )}
                        {revision.status === 'SCHEDULED' && (
                          <div className="mt-4 flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/integrations/${integration.id}/revisions/${revision.id}/config`)}
                              className="flex-1 sm:flex-none text-xs sm:text-sm whitespace-nowrap"
                            >
                              View Config
                            </Button>
                            {/* TODO: Implement reschedule functionality - allow user to change scheduled deployment time */}
                            <Button
                              variant="outline"
                              size="sm"
                              disabled
                              className="flex-1 sm:flex-none text-xs sm:text-sm"
                            >
                              Reschedule
                            </Button>
                            <Tooltip content={revision.allowed_actions?.reasons?.unschedule}>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelSchedule(integration.id)}
                                disabled={!revision.allowed_actions?.can_unschedule || deployingRevisionId === 'cancelling'}
                                className="flex-1 sm:flex-none text-xs sm:text-sm"
                              >
                                {deployingRevisionId === 'cancelling' ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Cancelling...
                                  </>
                                ) : (
                                  'Cancel'
                                )}
                              </Button>
                            </Tooltip>
                          </div>
                        )}
                        {revision.status === 'ARCHIVED' && (
                          <div className="mt-4 flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setViewingConfig(revision)}
                              disabled={!revision.config_json}
                            >
                              View Config
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <p className="text-sm text-muted-foreground">No revisions found</p>
                    <Button
                      size="sm"
                      onClick={() => handleCreateNewVersion(integration.id)}
                    >
                      Create New Version
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {integrations.length === 0 && !loading && (
        <Card>
          <CardContent className="p-12">
            <p className="text-center text-muted-foreground">No integrations found for this client</p>
          </CardContent>
        </Card>
      )}

      {/* Configuration Viewer Modal */}
      {viewingConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Configuration - Version {viewingConfig.revision_number}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {viewingConfig.status === 'LIVE' ? 'Live Configuration' :
                    viewingConfig.status === 'DRAFT' ? 'Draft Configuration' : 'Archived Configuration'}
                </p>
              </div>
              <button
                onClick={() => setViewingConfig(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1">
              <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-sm font-mono">
                {JSON.stringify(viewingConfig.config_json, null, 2)}
              </pre>
            </div>
            <div className="p-6 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setViewingConfig(null)}>
                Close
              </Button>
              <Button
                onClick={() => {
                  navigate(`/integrations/${viewingConfig.integration}/revisions/${viewingConfig.id}/config`);
                  setViewingConfig(null);
                }}
              >
                Edit Configuration
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Dialog */}
      <Dialog open={isLogsDialogOpen} onOpenChange={handleLogsDialogClose}>
        <DialogContent onClose={handleLogsDialogClose} className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Integration Logs</DialogTitle>
            <DialogDescription>
              Load events for {selectedIntegration?.display_name}
            </DialogDescription>
          </DialogHeader>

          {/* Search Bar */}
          {!logsLoading && !logsError && logs.length > 0 && (
            <div className="px-6 pb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search by file name, status, trigger type, or version..."
                  value={logsSearchQuery}
                  onChange={(e) => setLogsSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              {logsSearchQuery && (
                <p className="text-xs text-muted-foreground mt-2">
                  Showing {filteredLogs.length} of {logs.length} log{logs.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          )}

          <div className="flex-1 overflow-auto px-6">
            {logsLoading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}

            {logsError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-4">
                Error loading logs: {logsError}
              </div>
            )}

            {!logsLoading && !logsError && logs.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No load events found for this integration
              </div>
            )}

            {!logsLoading && !logsError && logs.length > 0 && filteredLogs.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No logs match your search criteria
              </div>
            )}

            {!logsLoading && !logsError && filteredLogs.length > 0 && (
              <div className="space-y-4 pb-4">
                {filteredLogs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-4 space-y-3 hover:border-gray-400 transition-colors">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm break-words select-all">{log.file_name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          Version {log.revision_number} • {log.trigger_type}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={
                            log.status === 'SUCCESS' ? 'success' :
                              log.status === 'FAILED' ? 'destructive' :
                                log.status === 'PROCESSING' ? 'warning' :
                                  'secondary'
                          }
                        >
                          {log.status}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyLog(log)}
                          className="h-8 w-8 p-0"
                          title="Copy log details"
                        >
                          {copiedLogId === log.id ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                      <div>
                        <span className="text-muted-foreground block">Started</span>
                        <span className="font-medium select-all">
                          {new Date(log.started_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </span>
                      </div>
                      {log.completed_at && (
                        <div>
                          <span className="text-muted-foreground block">Completed</span>
                          <span className="font-medium select-all">
                            {new Date(log.completed_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                            })}
                          </span>
                        </div>
                      )}
                      {log.duration_seconds !== null && (
                        <div>
                          <span className="text-muted-foreground block">Duration</span>
                          <span className="font-medium select-all">{log.duration_seconds}s</span>
                        </div>
                      )}
                      {log.file_size_bytes && (
                        <div>
                          <span className="text-muted-foreground block">File Size</span>
                          <span className="font-medium select-all">
                            {(log.file_size_bytes / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-muted-foreground block">Records Processed</span>
                        <span className="font-medium select-all">{log.records_processed.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block">Records Failed</span>
                        <span className={`font-medium select-all ${log.records_failed > 0 ? 'text-red-600' : ''}`}>
                          {log.records_failed.toLocaleString()}
                        </span>
                      </div>
                      {log.records_processed > 0 && (
                        <div>
                          <span className="text-muted-foreground block">Success Rate</span>
                          <span className="font-medium select-all">
                            {((log.records_processed - log.records_failed) / log.records_processed * 100).toFixed(1)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleLogsDialogClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Version Choice Dialog */}
      <Dialog open={isNewVersionDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsNewVersionDialogOpen(false);
          setNewVersionIntegrationId(null);
        }
      }}>
        <DialogContent onClose={() => {
          setIsNewVersionDialogOpen(false);
          setNewVersionIntegrationId(null);
        }}>
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
            <DialogDescription>
              How would you like to start your new configuration?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4 px-4"
              onClick={() => handleNewVersionChoice(true)}
            >
              <div className="text-left">
                <div className="font-semibold">Copy from LIVE</div>
                <div className="text-sm text-muted-foreground">
                  Start with the current live configuration as a base
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4 px-4"
              onClick={() => handleNewVersionChoice(false)}
            >
              <div className="text-left">
                <div className="font-semibold">Start Fresh</div>
                <div className="text-sm text-muted-foreground">
                  Begin with an empty configuration
                </div>
              </div>
            </Button>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsNewVersionDialogOpen(false);
                setNewVersionIntegrationId(null);
              }}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Integration Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
        if (!open) {
          handleDialogClose();
        }
      }}>
        <DialogContent onClose={handleDialogClose}>
          <DialogHeader>
            <DialogTitle>Add New Integration</DialogTitle>
            <DialogDescription>
              Create a new data integration for {client?.name}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddIntegration}>
            <div className="space-y-4 py-4">
              {/* Integration Type */}
              <div className="space-y-2">
                <label htmlFor="content_type" className="text-sm font-medium">
                  Integration Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="content_type"
                  value={formData.content_type}
                  onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                >
                  <option value="">Select integration type...</option>
                  {contentTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* TPA Name / Display Name */}
              <div className="space-y-2">
                <label htmlFor="display_name" className="text-sm font-medium">
                  Display Name <span className="text-red-500">*</span>
                </label>
                <Input
                  id="display_name"
                  type="text"
                  placeholder="e.g., HCSC Claims, Aetna Eligibility"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  required
                />
              </div>

              {/* Carrier */}
              <div className="space-y-2">
                <label htmlFor="carrier" className="text-sm font-medium">
                  Carrier
                </label>
                <select
                  id="carrier"
                  value={formData.carrier}
                  onChange={(e) => setFormData({ ...formData, carrier: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">No carrier (e.g., Eligibility)</option>
                  {carriers.map((carrier) => {
                    const isHcsc = carrier.name?.toLowerCase() === 'hcsc';
                    return (
                      <option
                        key={carrier.id}
                        value={carrier.id}
                        disabled={!isHcsc}
                        style={!isHcsc ? { color: '#9ca3af' } : {}}
                      >
                        {carrier.name}{carrier.subtitle ? ` (${carrier.subtitle})` : ''}
                      </option>
                    );
                  })}
                </select>
                <p className="text-xs text-muted-foreground">
                  Select a carrier for claims integrations, or leave empty for eligibility files.
                </p>
              </div>

              {formError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                  {formError}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleDialogClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Integration'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
